import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";

import date from "date-and-time";

import { ArrowDownIcon, ArrowUpIcon, CameraIcon } from "@heroicons/react/24/outline";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import { ITruck } from "../../models/Truck";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";
import { useAuth } from "../../contexts/AuthContext";
import { useUserLookup } from "../../hooks/useUserLookup";
import useTrucks from "../../data/useTrucks";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";


const colHelper = createColumnHelper<ITruck>();

export default function TrucksTable({ trucks, setTruck, }: { trucks: ITruck[]; setTruck: any; }) {
  const { updateOrder } = useTrucks();
  const { saveHandlers } = useDefaultCRUDHandlers("Order");
  const { isLoading: usersLoading, findUser } = useUserLookup();
  const { loading } = useAuth();
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleOrderChange = (truck: ITruck, direction: number) => {

    const maxDisplayOrder = Math.max(...trucks.map((t) => t.displayOrder));
    let nextOrder = maxDisplayOrder + 1;

    // Fill in 0s with unique displayOrders first
    trucks.forEach((truck) => {
      if (truck.displayOrder === 0) {
        truck.displayOrder = nextOrder++;
      }
    });

    const newTrucks = [...trucks];
    const index = newTrucks.findIndex((t) => t.id === truck.id);
    if (index === -1) return;

    const currentOrder = newTrucks[index].displayOrder;
    const newOrder = currentOrder + direction;

    if (newOrder < 1 || newOrder > newTrucks.length) return;

    const swapIndex = newTrucks.findIndex((t) => t.displayOrder === newOrder);
    if (swapIndex === -1) return;

    // Swap displayOrder values
    newTrucks[index].displayOrder = newOrder;
    newTrucks[swapIndex].displayOrder = currentOrder;

    updateOrder.mutate(newTrucks, saveHandlers);
  }

  const cols = [
    colHelper.display({
      id: "change-order",
      header: "Order",
      cell: (info) => (
        <div
          className="flex justify-center items-center flex-1 gap-x-2"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowUpIcon
            className="h-6 w-6 text-gray-500 hover:text-gray-800 transition duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleOrderChange(info.row.original, -1);
            }}
          />
          <ArrowDownIcon
            className="h-6 w-6 text-gray-500 hover:text-gray-800 transition duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleOrderChange(info.row.original, 1);
            }}
          />
        </div>
      ),
    }),
    colHelper.display({
      id: "image",
      cell: (info) => (
        <span className="flex justify-center relative">
          {info.row.original.vehicle?.vehicleImageUrl ? (
            <>
              <img
                className="w-32 h-20 object-contain"
                src={`${info.row.original.vehicle != null
                  ? info.row.original.vehicle.vehicleImageUrl
                  : null
                  }`}
              />
            </>
          ) : (
            <CameraIcon className="w-14 text-gray-600" />
          )}
        </span>
      ),
    }),
    colHelper.accessor("name", {
      header: "Name",
      sortingFn: "auto",
    }),
    colHelper.accessor("thirdParty", {
      header: "Type",
      cell: (info) => (
        <span>
          {info.getValue() ? "Third Party" : "Own Truck"}
        </span>
      ),
    }),
    colHelper.accessor("vehicle.vrm", {
      header: "VRM",
      enableSorting: false,
      cell: (info) => <span>{info.row.original.vehicle?.vrm ?? "Not Set"}</span>,
    }),
    colHelper.accessor("vehicle.combinedMake", {
      header: "Make",
      cell: (info) => <span>{info.row.original.vehicle?.combinedMake ?? "Not Set"}</span>,
    }),
    colHelper.accessor("vehicle.combinedModel", {
      header: "Model",
      cell: (info) => <span>{info.row.original.vehicle?.combinedModel ?? "Not Set"}</span>,
    }),
    colHelper.accessor("tripCount", {
      header: "Trips",
    }),
    colHelper.accessor("capacity", {
      header: "Capacity",
    }),
    colHelper.accessor("motDueDate", {
      header: "MOT Due",
      sortingFn: "auto",
      cell: (motDue) => (
        <span>
          {motDue.getValue() !== null
            ? date.format(new Date(motDue.getValue()!), "DD/MM/YYYY")
            : "Not Set"}
        </span>
      ),
    }),
    colHelper.accessor("serviceDueDate", {
      header: "Service Due",
      sortingFn: "auto",
      cell: (serviceDue) => (
        <span>
          {serviceDue.getValue() !== null
            ? date.format(new Date(serviceDue.getValue()!), "DD/MM/YYYY")
            : "Not Set"}
        </span>
      ),
    }),
    colHelper.display({
      header: "Default Driver",
      cell: (info) => {
        const user = findUser(info.row.original.driverId!);
        return (
          <>
            {user?.forename} {user?.surname}
          </>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: trucks,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading || usersLoading) {
    return (
      <div className="pt-[15vh]">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <>
        <div className="mt-4 bg-white overflow-auto">
          <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="bg-primary-50 text-gray-800"
                >
                  {headerGroup.headers.map((header, i) => (
                    <th
                      key={header.id}
                      scope="col"
                      className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: " ▲",
                            desc: " ▼",
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, i) => {
                let rowCells = row.getVisibleCells();
                return (
                  <Fragment key={i}>
                    <tr
                      key={row.id}
                      onClick={() => setTruck(row.original)}
                      className={classNames(
                        row.original.active ? "" : "bg-gray-200",
                        "hover:bg-gray-100 hover:cursor-pointer"
                      )}
                    >
                      {rowCells.map((cell) => (
                        <td key={cell.id} className="px-3 py-3 border text-sm">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    {row.getIsExpanded() && (
                      <></>
                      // <tr>
                      //   {/* 2nd row is a custom 1 cell row */}
                      //   <td colSpan={row.getVisibleCells().length}>
                      //     <LeadsTableSubComponent lead={row.original} />
                      //   </td>
                      // </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }
}
